import { useState, useEffect } from "react";
import { PrimaryButton, GhostButton, SecondaryButton } from "@/components/StandardButton";
import { Copy, Download, Mail, MessageSquare, Check, Lock, ExternalLink, Eye } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { DisplayM, BodyText, Caption } from "@/components/ui/typography";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { generateFeedbackLink } from "@/utils/feedbackLinks";
import { supabase } from "@/integrations/supabase/client";
import { generatePasscode } from "@/utils/passcodeUtils";

interface ShareStepProps {
  pageSlug: string;
  companyName: string;
  onNext: () => void;
  onSkip: () => void;
}

export const ShareStep = ({ pageSlug, companyName, onNext, onSkip }: ShareStepProps) => {
  const [copied, setCopied] = useState(false);
  const [passcodeCopied, setPasscodeCopied] = useState(false);
  const [passcode, setPasscode] = useState<string | null>(null);
  const [passcodeEnabled, setPasscodeEnabled] = useState(true);
  const [togglingPasscode, setTogglingPasscode] = useState(false);
  const { toast } = useToast();

  const feedbackUrl = generateFeedbackLink(companyName, pageSlug);
  const previewUrl = `${feedbackUrl}?preview=true`;

  useEffect(() => {
    const fetchPasscode = async () => {
      const { data } = await supabase
        .from("feedback_pages")
        .select("passcode")
        .eq("slug", pageSlug)
        .single();
      setPasscode(data?.passcode ?? null);
      setPasscodeEnabled(!!data?.passcode);
    };
    fetchPasscode();
  }, [pageSlug]);

  const handleTogglePasscode = async (enabled: boolean) => {
    setTogglingPasscode(true);
    try {
      const newPasscode = enabled ? (passcode || generatePasscode()) : null;
      const { error } = await supabase
        .from("feedback_pages")
        .update({ passcode: newPasscode })
        .eq("slug", pageSlug);

      if (error) throw error;
      setPasscode(newPasscode);
      setPasscodeEnabled(enabled);
      toast({
        title: enabled ? "Access code enabled" : "Access code disabled",
        description: enabled
          ? "Recipients will need this code to submit feedback"
          : "Anyone with the link can submit feedback",
      });
    } catch (err: any) {
      toast({ title: "Failed to update", description: err.message, variant: "destructive" });
    } finally {
      setTogglingPasscode(false);
    }
  };

  const copy = async (text: string, label: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      toast({ title: `${label} copied!` });
      setTimeout(() => setter(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  const handleDownloadQR = () => {
    const svg = document.querySelector('#qr-code-svg svg') as SVGElement;
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, 300, 300);
      }
      const link = document.createElement('a');
      link.download = `${companyName}-feedback-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast({ title: "QR Code downloaded!" });
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Share your feedback with ${companyName}`);
    const passcodeText = passcode ? `\n\n🔐 Access Code: ${passcode}\n(You'll need this code to submit feedback)` : '';
    const body = encodeURIComponent(
      `Hi,\n\nWe'd love to hear your honest feedback about ${companyName}.\n\nAll responses are 100% anonymous.\n\nShare your thoughts here: ${feedbackUrl}${passcodeText}\n\nThank you!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleSlackShare = () => {
    const passcodeText = passcode ? ` | 🔐 Access Code: ${passcode}` : '';
    const text = encodeURIComponent(
      `📣 Share your feedback with ${companyName}!\n\nAll responses are 100% anonymous: ${feedbackUrl}${passcodeText}`
    );
    window.open(`https://slack.com/share?text=${text}`, '_blank');
  };

  return (
    <div className="p-2 sm:p-4 min-w-0 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="text-center mb-4">
        <DisplayM className="mb-1">Share your page</DisplayM>
        <BodyText className="text-muted-foreground text-sm">
          Your feedback page is ready. Here's how to share it.
        </BodyText>
      </div>

      {/* Copy Link */}
      <div className="bg-muted/50 rounded-2xl p-2 border mb-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <code className="flex-1 min-w-0 text-xs sm:text-sm truncate bg-background rounded-2xl px-2 py-2 border block">
            {feedbackUrl}
          </code>
          <PrimaryButton
            onClick={() => copy(feedbackUrl, "Link", setCopied)}
            size="sm"
            className="gap-1.5 shrink-0"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="hidden sm:inline">{copied ? "Copied!" : "Copy"}</span>
          </PrimaryButton>
        </div>
      </div>

      {/* Access Code toggle + display */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Lock className="h-4 w-4 text-primary shrink-0" />
            <Label htmlFor="passcode-toggle" className="text-sm font-medium cursor-pointer">
              Require access code
            </Label>
          </div>
          <Switch
            id="passcode-toggle"
            checked={passcodeEnabled}
            onCheckedChange={handleTogglePasscode}
            disabled={togglingPasscode}
          />
        </div>

        {passcodeEnabled && passcode ? (
          <div className="mt-3 flex items-center justify-between gap-2 bg-background rounded-2xl px-3 py-2 border border-primary/20">
            <div className="min-w-0">
              <span className="text-xs text-muted-foreground">Code: </span>
              <span className="font-mono font-bold text-primary text-base">{passcode}</span>
            </div>
            <SecondaryButton
              onClick={() => copy(passcode, "Access code", setPasscodeCopied)}
              size="sm"
              className="gap-1.5 shrink-0 h-8 px-2"
            >
              {passcodeCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </SecondaryButton>
          </div>
        ) : (
          <Caption className="text-muted-foreground mt-2 block">
            {passcodeEnabled
              ? "Generating access code…"
              : "Anyone with the link can submit feedback."}
          </Caption>
        )}
      </div>

      {/* Preview + QR side-by-side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 items-stretch">
        {/* QR */}
        <div className="flex flex-col items-center justify-center gap-2 bg-muted/30 rounded-2xl p-3 border">
          <div
            className="bg-white p-2 rounded-2xl border shadow-sm relative group cursor-pointer"
            onClick={handleDownloadQR}
            id="qr-code-svg"
            role="button"
            aria-label="Download QR code"
          >
            <QRCodeSVG value={feedbackUrl} size={96} />
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
              <span className="text-white flex items-center gap-1.5 text-xs font-medium">
                <Download className="h-3.5 w-3.5" /> Download
              </span>
            </div>
          </div>
          <Caption className="text-muted-foreground text-xs text-center">
            Scan to preview · click to download
          </Caption>
        </div>

        {/* Preview / Share buttons */}
        <div className="flex flex-col gap-2 justify-center">
          <SecondaryButton
            onClick={() => window.open(previewUrl, '_blank')}
            className="gap-1.5 justify-center min-h-[40px]"
          >
            <Eye className="h-4 w-4" /> Preview page
            <ExternalLink className="h-3.5 w-3.5 opacity-60" />
          </SecondaryButton>
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton onClick={handleEmailShare} size="sm" className="gap-1.5 justify-center">
              <Mail className="h-4 w-4" /> Email
            </SecondaryButton>
            <SecondaryButton onClick={handleSlackShare} size="sm" className="gap-1.5 justify-center">
              <MessageSquare className="h-4 w-4" /> Slack
            </SecondaryButton>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <GhostButton onClick={onSkip} className="flex-1 min-h-[44px] text-sm">
          I'll do this later
        </GhostButton>
        <PrimaryButton onClick={onNext} className="flex-1 min-h-[44px] text-sm">
          Next: Schedule Emails
        </PrimaryButton>
      </div>
    </div>
  );
};
